import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Chip,
  Slider
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import BaseDialog from "../../utils/dialog/BaseDialog";
import MappingSettings, {
  SensitiveDataAlert
} from "../../Common/ClassificationEngine/MappingSettings";
import { DropDownByObj } from "../../utils/Form/Dropdown";
import { JustInfo } from "../../utils/Icon";
import {
  investmentTaxAdjustmentOptions_tagging,
  taxAdjustmentOptions_tagging
} from "../../../utils/static/detailsPL";
import { formatAmount } from "../../../utils/hooks/dataHandler";
import { alpha, useTheme } from "@mui/material/styles";
import { headerStyle } from "../../MainTaxComputation/utils/DetailsTable";
import { PROCESSING_OPTION_LABELS } from "./processingOptionLabels";
import InsightsIcon from "@mui/icons-material/Insights";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { generateExcelMultipleSheets } from "../../../utils/data/excelData";

const SECTION_OPTIONS = [
  "Revenue",
  "Other Income",
  "Cost of Sales",
  "Expenses",
  "Ignore"
];

const getTaxAdjustmentOptions = (companyType) =>
  companyType === "investment"
    ? investmentTaxAdjustmentOptions_tagging
    : taxAdjustmentOptions_tagging;

const getTaxComputationType = (companyType) =>
  companyType === "investment" ? "investment" : companyType || "common";

const DOMAIN_KEYS = [
  {
    key: "accountCode",
    label: "Account Code",
    required: false,
    type: "string"
  },
  { key: "accountName", label: "Account Name", required: true, type: "string" },
  { key: "reference", label: "Reference", required: false, type: "string" },
  { key: "amount", label: "Amount", required: true, type: "numeric" },
  {
    key: "taxAdjustment",
    label: "Tax Adjustment",
    required: false,
    type: "enum"
  },
  {
    key: "section",
    label: "Section",
    required: false,
    type: "enum",
    options: SECTION_OPTIONS
  }
];

const UNIQUE_DOMAIN_KEYS = new Set(
  DOMAIN_KEYS.map((domainKey) => domainKey.key).filter(
    (key) => key !== "amount"
  )
);
const REQUIRED_DOMAIN_KEY_ORDER = ["accountName", "amount"];
const ORDERED_DOMAIN_KEYS = [
  ...REQUIRED_DOMAIN_KEY_ORDER.map((key) =>
    DOMAIN_KEYS.find((domainKey) => domainKey.key === key)
  ).filter(Boolean),
  ...DOMAIN_KEYS.filter(
    (domainKey) => !REQUIRED_DOMAIN_KEY_ORDER.includes(domainKey.key)
  )
];

const ASSIGNMENT_WARNING =
  "Column values are always processed from the original extract. Choose Unassigned or Reset to restore the original values.";

const SNAPSHOT_COLUMN_ORDER = [
  "accountCode",
  "accountName",
  "reference",
  "amount",
  "taxAdjustment",
  "section"
];

const DOMAIN_LABELS = Object.fromEntries(
  DOMAIN_KEYS.map((domainKey) => [domainKey.key, domainKey.label])
);

const SENSITIVE_MARKUP_PATTERN = /<[^<>]+>/;
const SENSITIVE_MARKUP_SPLIT_PATTERN = /(<[^<>]+>)/g;
const SENSITIVE_MARKUP_TOOLTIP =
  "This value was redacted as sensitive during extraction. Replace it with the original text so this line item is complete.";

const containsSensitiveMarkup = (value) =>
  SENSITIVE_MARKUP_PATTERN.test(String(value ?? ""));

const countSensitiveMarkup = (records) => {
  let fieldCount = 0;
  let rowCount = 0;

  records.forEach((record) => {
    const values =
      record?.cells != null
        ? Object.values(record.cells)
        : Object.values(record || {});
    const hits = values.filter(containsSensitiveMarkup).length;
    if (hits) {
      rowCount += 1;
      fieldCount += hits;
    }
  });

  return { fieldCount, rowCount };
};

const PlaceholderText = ({ children }) => (
  <Typography
    component='span'
    variant='inherit'
    sx={{ color: "error.main", fontStyle: "italic" }}
  >
    {children}
  </Typography>
);

const HighlightedMarkupText = ({ value, emptyLabel = "—" }) => {
  const text = String(value ?? "");
  if (!text) {
    return (
      <Typography component='span' variant='inherit' color='text.secondary'>
        {emptyLabel}
      </Typography>
    );
  }

  const parts = text.split(SENSITIVE_MARKUP_SPLIT_PATTERN);
  if (parts.length === 1) return text;

  return parts.map((part, index) =>
    SENSITIVE_MARKUP_PATTERN.test(part) ? (
      <Tooltip key={`${part}-${index}`} title={SENSITIVE_MARKUP_TOOLTIP}>
        <Box
          component='span'
          sx={{
            color: "error.main",
            fontWeight: 700,
            fontStyle: "italic",
            borderBottom: "1px dashed",
            borderColor: "error.main",
            cursor: "help"
          }}
        >
          {part}
        </Box>
      </Tooltip>
    ) : (
      part
    )
  );
};

const AI_PROCESSING_CONSENT_VERSION = "ai-processing-ready-v2";
const AI_PROCESSING_CONSENT_ID = "ai-processing-ready-consent";
const SNAPSHOT_HIDDEN_VALUE = "[hidden]";
const SNAPSHOT_MASKABLE_KEYS = new Set([
  "accountName",
  "accountCode",
  "reference"
]);
const SNAPSHOT_ZOOM_MIN = 0.85;
const SNAPSHOT_ZOOM_MAX = 1.4;
const SNAPSHOT_ZOOM_STEP = 0.15;

const sanitizeSnapshotForAi = (rows = []) =>
  rows
    .filter((row) => !row.hiddenFromAi)
    .map(({ hiddenFromAi, _hiddenValues, ...row }) => row);

const downloadSnapshotExcel = (rows, columns) => {
  const data = rows.map((row, index) => {
    const record = { "#": index + 1 };
    columns.forEach((key) => {
      record[DOMAIN_LABELS[key] || key] = row[key] ?? "";
    });
    record.Status = row.hiddenFromAi ? "Hidden from AI" : "Included";
    return record;
  });
  const stamp = new Date().toISOString().slice(0, 10);
  generateExcelMultipleSheets({
    fileName: `ai-processing-snapshot-${stamp}.xlsx`,
    sheets: [{ name: "Snapshot", data }]
  });
};

const SNAPSHOT_TASK_SUMMARIES = {
  plCategory:
    "Place each line into Revenue, Other Income, Cost of Sales, Expenses, or Ignore.",
  taxAdjustment:
    "Suggest a tax treatment such as None, Added Back, Deduct, or a tagged schedule.",
  mergeAccounts: "Combine similar account names into one line.",
  divergent: "How far AI may infer when a line is ambiguous."
};

const SnapshotPurposePanel = ({ options, includedCount }) => (
  <Box
    sx={{
      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
      border: "1px solid",
      borderColor: (theme) => alpha(theme.palette.primary.main, 0.12),
      borderRadius: 1.5,
      px: 1.5,
      py: 1.25
    }}
  >
    <Typography
      variant='overline'
      color='text.secondary'
      sx={{ display: "block", fontWeight: 700, letterSpacing: 0.8, lineHeight: 1.2 }}
    >
      What this snapshot is for
    </Typography>
    <Typography variant='body2' sx={{ mt: 0.5, lineHeight: 1.45 }}>
      AI will classify the {includedCount} line
      {includedCount === 1 ? "" : "s"} below. It will not file a return or
      change Detailed P&amp;L until you apply the result in the next step.
    </Typography>
    {options.length > 0 && (
      <Stack spacing={0.75} sx={{ mt: 1 }}>
        {options.map((option) => (
          <Stack
            key={option.key}
            direction='row'
            spacing={1}
            alignItems='flex-start'
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                bgcolor: "primary.main",
                borderRadius: "50%",
                flexShrink: 0,
                height: 6,
                mt: 0.7,
                width: 6
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Stack direction='row' spacing={0.5} alignItems='center'>
                <Typography variant='body2' fontWeight={700} lineHeight={1.3}>
                  {option.label}
                </Typography>
                <JustInfo title={option.tooltip} ml={0} mr={0} />
              </Stack>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: "block", lineHeight: 1.35 }}
              >
                {SNAPSHOT_TASK_SUMMARIES[option.key] || option.detail}
                {option.key === "divergent" && option.detail
                  ? ` Currently ${option.detail}.`
                  : option.key === "taxAdjustment" && option.detail
                    ? ` ${option.detail}.`
                    : ""}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
    )}
  </Box>
);

const SnapshotReviewNotes = ({
  removedEmptyAmountCount = 0,
  sensitiveCounts = { fieldCount: 0, rowCount: 0 },
  hiddenRowCount = 0,
  hiddenValueCount = 0
}) => {
  const hasSensitive = sensitiveCounts.fieldCount > 0;
  const notes = [
    hasSensitive
      ? `${sensitiveCounts.fieldCount} pre-filtered placeholder${sensitiveCounts.fieldCount === 1 ? "" : "s"} in ${sensitiveCounts.rowCount} row${sensitiveCounts.rowCount === 1 ? "" : "s"} are highlighted in red. Check those cells before sending.`
      : "Review account names, codes, and references for personal or confidential data before sending.",
    removedEmptyAmountCount > 0
      ? `${removedEmptyAmountCount} empty-amount line${removedEmptyAmountCount === 1 ? " was" : "s were"} removed and will not be sent to AI.`
      : null,
    hiddenRowCount || hiddenValueCount
      ? `You hid ${hiddenRowCount} row${hiddenRowCount === 1 ? "" : "s"} and ${hiddenValueCount} cell${hiddenValueCount === 1 ? "" : "s"}. Hidden items stay in this preview so you can unhide them.`
      : "Hide a row or cell if a name or identifier should not go to AI. You can unhide it here."
  ].filter(Boolean);

  return (
    <Box
      sx={{
        bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
        border: "1px solid",
        borderColor: (theme) => alpha(theme.palette.warning.main, 0.45),
        borderRadius: 1.5,
        px: 1.5,
        py: 1.25
      }}
    >
      <Typography
        variant='overline'
        sx={{
          color: "warning.dark",
          display: "block",
          fontWeight: 700,
          letterSpacing: 0.8,
          lineHeight: 1.2
        }}
      >
        Review before sending
      </Typography>
      <Stack component='ul' spacing={0.5} sx={{ m: 0, mt: 0.75, pl: 2 }}>
        {notes.map((note) => (
          <Typography
            key={note}
            component='li'
            variant='body2'
            sx={{ color: "warning.dark", lineHeight: 1.4 }}
          >
            {note}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
};

const AiProcessingConsent = ({ checked, onChange }) => (
  <FormControlLabel
    sx={{
      alignItems: "flex-start",
      m: 0,
      width: "100%",
      "& .MuiFormControlLabel-label": { minWidth: 0, pt: 0.25 }
    }}
    control={
      <Checkbox
        id={AI_PROCESSING_CONSENT_ID}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        size='small'
        sx={{ mt: -0.25 }}
      />
    }
    label={
      <Box>
        <Typography variant='body2' sx={{ fontWeight: 600, lineHeight: 1.4 }}>
          I confirm this data is accurate and ready for AI processing.
        </Typography>
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ display: "block", mt: 0.25, lineHeight: 1.4 }}
        >
          Processed securely in compliance with workspace data policies.
        </Typography>
      </Box>
    }
  />
);

const formatFinancialAmount = (amount) => {
  if (amount === null || amount === undefined || amount === "") {
    return { text: "—", negative: false };
  }
  const numericValue = Number(amount);
  if (!Number.isFinite(numericValue)) {
    return { text: "—", negative: false };
  }
  return {
    text: formatAmount(numericValue, false, false, false, true),
    negative: numericValue < 0
  };
};

const FinancialAmount = ({ amount, variant = "body2" }) => {
  const { text, negative } = formatFinancialAmount(amount);
  return (
    <Typography
      variant={variant}
      sx={{
        color: negative ? "error.main" : "text.primary",
        fontWeight: negative ? 600 : 500
      }}
    >
      {text}
    </Typography>
  );
};

const PARTIAL_AMOUNT_PATTERN = /^-?\d*\.?\d*$/;

const TOOLTIPS = {
  plCategory:
    "Ask AI to place each line into a P&L section: Revenue, Other Income, Cost of Sales, Expenses, or Ignore. Use this when Section is blank or you want AI to classify category for you.",
  taxAdjustment:
    "Ask AI to suggest a tax treatment for each line (None, Added Back, Deduct, or a tagged schedule). Leave this off if you do not need tax tagging in this run.",
  mergeAccounts:
    "Ask AI to merge similar accounts into one line, using the P&L categories it assigns. Turn on P&L Category first. AI decides which rows belong together.",
  proceed:
    "Map Account Name and at least one Amount column, and turn on at least one AI task. You will review a snapshot before classification starts. Multiple Amount columns are summed into one amount.",
  divergent:
    "Controls how far AI may infer when a line is ambiguous or has little supporting detail."
};

const DIVERGENT_LEVELS = [
  {
    value: "low",
    label: "Low",
    mode: "Strict audit",
    description:
      "Most conservative. If evidence is thin, keep the safest tax treatment, leave tagging blank, and do not force merges."
  },
  {
    value: "medium",
    label: "Medium",
    mode: "Balanced",
    description:
      "Follow common accounting practice to infer categories and merge when the industry convention is clear."
  },
  {
    value: "high",
    label: "High",
    mode: "Aggressive",
    description:
      "Actively match nearby schedules and merge similar accounts even when the evidence is only approximate."
  }
];
const DEFAULT_DIVERGENT = "medium";
const DIVERGENT_SLIDER_MARKS = DIVERGENT_LEVELS.map((level, index) => ({
  value: index,
  label: level.label
}));

const getDivergentLevel = (value) =>
  DIVERGENT_LEVELS.find((level) => level.value === value) || DIVERGENT_LEVELS[0];

const normalizeCellValue = (value) => {
  if (value === null || value === undefined) return "";
  return value;
};

const toSheetList = (sheetsObject) => {
  if (
    !sheetsObject ||
    typeof sheetsObject !== "object" ||
    Array.isArray(sheetsObject)
  ) {
    return [];
  }

  return Object.entries(sheetsObject)
    .map(([name, sheet]) => ({
      name,
      columns:
        sheet?.columns && typeof sheet.columns === "object"
          ? sheet.columns
          : {},
      rowCount: sheet?.rowCount
    }))
    .filter((sheet) => Object.keys(sheet.columns).length > 0);
};

const getExtractedSheets = (extractionResult) => {
  if (!extractionResult || typeof extractionResult !== "object") return [];

  const candidates = [
    extractionResult,
    extractionResult.data,
    extractionResult.extractedData,
    extractionResult.data?.extractedData,
    extractionResult.data?.data
  ].filter(Boolean);

  for (const candidate of candidates) {
    if (candidate.sheets) {
      const sheets = toSheetList(candidate.sheets);
      if (sheets.length) return sheets;
    }

    if (candidate.columns && typeof candidate.columns === "object") {
      return [
        {
          name: "Sheet 1",
          columns: candidate.columns,
          rowCount: candidate.rowCount
        }
      ];
    }
  }

  return [];
};

const buildTableState = (sheet) => {
  const columns = sheet?.columns || {};
  const columnNames = Object.keys(columns);
  const rowCount =
    sheet?.rowCount ??
    columnNames.reduce(
      (max, name) => Math.max(max, columns[name]?.length || 0),
      0
    );

  const rows = Array.from({ length: rowCount }, (_, rowIndex) => {
    const cells = Object.fromEntries(
      columnNames.map((name) => [
        name,
        normalizeCellValue(columns[name]?.[rowIndex])
      ])
    );
    return {
      id: `${sheet?.name || "sheet"}-row-${rowIndex}`,
      included: true,
      cells,
      originalCells: { ...cells }
    };
  });

  return { columnNames, rows };
};

const cloneTableRows = (rows) =>
  rows.map((row) => ({
    ...row,
    cells: { ...row.cells },
    originalCells: { ...row.originalCells }
  }));

const matchTaxAdjustmentOption = (value, taxAdjustmentOptions) => {
  const text = String(value ?? "").trim();
  if (!text || text === "None") {
    return (
      taxAdjustmentOptions.find((option) => option.label === "None") || {
        value: "",
        label: "None"
      }
    );
  }
  return taxAdjustmentOptions.find(
    (option) => option.label === text || option.value === text
  );
};

const coerceCellValue = (domainKey, value, taxAdjustmentOptions = []) => {
  if (!domainKey) return value ?? "";

  if (domainKey === "amount") {
    let text = String(value ?? "").trim();
    if (!text) return "";
    const wrappedNegative = /^\(.*\)$/.test(text);
    if (wrappedNegative) text = text.slice(1, -1).trim();
    text = text.replace(/,/g, "");
    if (text === "" || text === "-" || text === "." || text === "-.") return "";
    const numericValue = Number(text);
    if (!Number.isFinite(numericValue)) return "";
    const signedValue =
      wrappedNegative && numericValue > 0 ? -numericValue : numericValue;
    return String(signedValue);
  }

  if (domainKey === "taxAdjustment") {
    return matchTaxAdjustmentOption(value, taxAdjustmentOptions)?.value ?? "";
  }

  if (domainKey === "section") {
    const text = String(value ?? "");
    return SECTION_OPTIONS.includes(text) ? text : "";
  }

  return String(value ?? "");
};

const toPayloadValue = (domainKey, value) => {
  if (domainKey === "amount") {
    if (value === "" || value === null || value === undefined) return null;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }
  return value ?? "";
};

const isOriginallyEmptyAmount = (row, columnName) =>
  coerceCellValue("amount", row.originalCells?.[columnName]) === "";

const buildNormalizedRows = (rows, columnMapping) => {
  const mappedEntries = Object.entries(columnMapping).filter(
    ([, domainKey]) => domainKey
  );
  const amountColumns = mappedEntries
    .filter(([, domainKey]) => domainKey === "amount")
    .map(([columnName]) => columnName);
  const otherEntries = mappedEntries.filter(
    ([, domainKey]) => domainKey !== "amount"
  );

  const mappedRows = rows
    .filter((row) => row.included)
    .map((row) => {
      const record = Object.fromEntries(
        otherEntries.map(([columnName, domainKey]) => [
          domainKey,
          toPayloadValue(domainKey, row.cells[columnName])
        ])
      );

      if (amountColumns.length) {
        record.amount = amountColumns.reduce((sum, columnName) => {
          const numericValue = toPayloadValue("amount", row.cells[columnName]);
          return sum + (numericValue ?? 0);
        }, 0);
      }

      return record;
    });

  const keptRows = mappedRows.filter((record) => {
    const amount = Number(record.amount);
    return Number.isFinite(amount) && amount !== 0;
  });

  return {
    rows: keptRows,
    removedEmptyAmountCount: mappedRows.length - keptRows.length
  };
};

const getSnapshotColumns = (rows) =>
  SNAPSHOT_COLUMN_ORDER.filter((key) =>
    rows.some((row) => row[key] !== undefined)
  );

const SNAPSHOT_COLUMN_SX = {
  accountCode: { minWidth: 96, whiteSpace: "nowrap" },
  accountName: { minWidth: 168 },
  reference: { minWidth: 96 },
  amount: { minWidth: 120, whiteSpace: "nowrap" },
  taxAdjustment: { minWidth: 168 },
  section: { minWidth: 128, whiteSpace: "nowrap" }
};

const ProcessingOptionSummary = ({
  options,
  rowCount,
  totalNegative,
  totalText,
  variant = "classify"
}) => {
  if (!options.length && variant === "classify") return null;
  const isApply = variant === "apply";
  const summaryItems = [
    {
      key: "rows",
      label: isApply ? "Accounts to apply" : "Rows sent to AI",
      value: `${rowCount} ${
        isApply
          ? `account${rowCount === 1 ? "" : "s"}`
          : `row${rowCount === 1 ? "" : "s"}`
      }`,
      tooltip: isApply
        ? "Only the rows shown in the table below will be written to the detailed P&L."
        : "Only the rows shown in the table below will be sent for AI processing."
    },
    {
      key: "netAmount",
      label: "Net amount in snapshot",
      value: totalText,
      tooltip:
        "This is the total amount in this confirmation snapshot. Negative amounts are shown in red parentheses.",
      valueSx: {
        color: totalNegative ? "error.main" : "text.primary"
      }
    }
  ];

  return (
    <Box
      sx={{
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        border: "1px solid",
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.12),
        borderRadius: 1.5,
        mb: 1.5,
        minWidth: 0,
        p: { xs: 1, sm: 1.25 }
      }}
    >
      <Typography variant='subtitle2' fontWeight={800} lineHeight={1.25}>
        {isApply
          ? "Review classified accounts"
          : "Confirm before AI processing"}
      </Typography>
      <Typography
        variant='caption'
        color='text.secondary'
        sx={{ display: "block", mt: 0.25 }}
      >
        {isApply
          ? "These are the classified accounts that will be written to the detailed P&L after you confirm."
          : "We've prepared this sanitized snapshot. Confirm the rows and AI tasks, then continue."}
      </Typography>

      <Typography
        variant='caption'
        color='text.secondary'
        fontWeight={700}
        sx={{ display: "block", mt: 1 }}
      >
        Submission summary
      </Typography>
      <Stack component='ul' spacing={0.75} sx={{ m: 0, mt: 0.75, pl: 2 }}>
        {summaryItems.map((item) => (
          <Stack
            component='li'
            key={item.key}
            direction='row'
            alignItems='center'
            spacing={0.75}
            sx={{ minWidth: 0 }}
          >
            <Box
              component='span'
              sx={{
                bgcolor: "primary.main",
                borderRadius: "50%",
                flexShrink: 0,
                height: 6,
                width: 6
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant='body2' lineHeight={1.25}>
                <Box component='span' sx={{ fontWeight: 700 }}>
                  {item.label}:
                </Box>{" "}
                <Box
                  component='span'
                  sx={{
                    fontWeight: 800,
                    ...item.valueSx
                  }}
                >
                  {item.value}
                </Box>
              </Typography>
            </Box>
            <JustInfo title={item.tooltip} ml={0} mr={0} />
          </Stack>
        ))}
      </Stack>

      {options.length > 0 && (
        <Box
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            mt: 1,
            pt: 1
          }}
        >
          <Typography variant='caption' color='text.secondary' fontWeight={700}>
            {variant === "apply"
              ? "Classification settings"
              : "AI tasks selected"}
          </Typography>
        <Stack component='ul' spacing={0.75} sx={{ m: 0, mt: 0.75, pl: 2 }}>
          {options.map((option) => (
            <Stack
              component='li'
              key={option.key}
              direction='row'
              alignItems='center'
              spacing={0.75}
              sx={{ minWidth: 0 }}
            >
              <Box
                component='span'
                sx={{
                  bgcolor: "primary.main",
                  borderRadius: "50%",
                  flexShrink: 0,
                  height: 6,
                  width: 6
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant='body2' fontWeight={700} lineHeight={1.25}>
                  {option.label}
                </Typography>

                {option.detail && (
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: "block", lineHeight: 1.25 }}
                  >
                    {option.detail}
                  </Typography>
                )}
              </Box>

              <JustInfo title={option.tooltip} ml={0} mr={0} />
            </Stack>
          ))}
        </Stack>
        </Box>
      )}
    </Box>
  );
};

const SnapshotEditableValue = ({
  fieldKey,
  value,
  taxAdjustmentOptions,
  onCommit,
  onMask,
  onUnmask
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ""));
  const canMask = SNAPSHOT_MASKABLE_KEYS.has(fieldKey);
  const isMasked = String(value ?? "") === SNAPSHOT_HIDDEN_VALUE;

  useEffect(() => {
    if (!editing) setDraft(String(value ?? ""));
  }, [editing, value]);

  const displayValue = () => {
    if (isMasked) {
      return (
        <Typography
          component='span'
          variant='inherit'
          sx={{ color: "text.secondary", fontStyle: "italic" }}
        >
          Hidden from AI
        </Typography>
      );
    }
    if (fieldKey === "amount") return <FinancialAmount amount={value} />;
    if (fieldKey === "taxAdjustment") {
      const option = matchTaxAdjustmentOption(value, taxAdjustmentOptions);
      return option?.label || value || "None";
    }
    return <HighlightedMarkupText value={value} />;
  };

  if (fieldKey === "taxAdjustment" || fieldKey === "section") {
    return displayValue();
  }

  if (editing) {
    return (
      <TextField
        autoFocus
        fullWidth
        size='small'
        variant='standard'
        value={draft}
        onChange={(event) => {
          const nextValue = event.target.value;
          if (fieldKey === "amount" && !PARTIAL_AMOUNT_PATTERN.test(nextValue)) {
            return;
          }
          setDraft(nextValue);
        }}
        onBlur={() => {
          onCommit(
            fieldKey === "amount" ? coerceCellValue("amount", draft) : draft
          );
          setEditing(false);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
          if (event.key === "Escape") setEditing(false);
        }}
      />
    );
  }

  return (
    <Stack
      direction='row'
      alignItems='center'
      spacing={0.25}
      sx={{ minWidth: 0 }}
    >
      <Box
        onClick={() => setEditing(true)}
        sx={{
          cursor: "text",
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis"
        }}
      >
        {displayValue()}
      </Box>
      {canMask && (
        <Tooltip title={isMasked ? "Unhide this value" : "Hide this value from AI"}>
          <IconButton
            size='small'
            aria-label={
              isMasked ? "Unhide this value" : "Hide this value from AI"
            }
            onClick={(event) => {
              event.stopPropagation();
              if (isMasked) onUnmask();
              else onMask();
            }}
            sx={{ flexShrink: 0, height: 24, width: 24 }}
          >
            {isMasked ? (
              <VisibilityIcon sx={{ fontSize: 16 }} />
            ) : (
              <VisibilityOffIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

const SnapshotReviewTable = ({
  rows,
  taxAdjustmentOptions,
  processingOptions,
  removedEmptyAmountCount = 0,
  sensitiveCounts = { fieldCount: 0, rowCount: 0 },
  showProcessingSummary = true,
  variant = "classify",
  onRowsChange
}) => {
  const theme = useTheme();
  const [zoom, setZoom] = useState(1);
  const columns = getSnapshotColumns(rows);
  const canEditSnapshot = variant === "classify" && Boolean(onRowsChange);
  const includedRows = rows.filter((row) => !row.hiddenFromAi);
  const hiddenRowCount = rows.length - includedRows.length;
  const hiddenValueCount = rows.reduce(
    (count, row) =>
      count +
      Object.values(row._hiddenValues || {}).filter(
        (value) => value !== undefined
      ).length,
    0
  );
  const totalAmount = includedRows.reduce(
    (sum, row) => sum + (Number(row.amount) || 0),
    0
  );
  const { text: totalText, negative: totalNegative } =
    formatFinancialAmount(totalAmount);
  const selectedProcessingOptions = [
    processingOptions?.plCategory && {
      key: "plCategory",
      label: PROCESSING_OPTION_LABELS.plCategory,
      tooltip: TOOLTIPS.plCategory
    },
    processingOptions?.taxAdjustment && {
      key: "taxAdjustment",
      label: PROCESSING_OPTION_LABELS.taxAdjustment,
      detail: processingOptions.taxComputationType
        ? `Tax computation: ${processingOptions.taxComputationType}`
        : undefined,
      tooltip: TOOLTIPS.taxAdjustment
    },
    processingOptions?.mergeAccounts && {
      key: "mergeAccounts",
      label: PROCESSING_OPTION_LABELS.mergeAccounts,
      tooltip: TOOLTIPS.mergeAccounts
    },
    processingOptions?.divergent && {
      key: "divergent",
      label: `Inference: ${getDivergentLevel(processingOptions.divergent).label}`,
      detail: getDivergentLevel(processingOptions.divergent).mode,
      tooltip: TOOLTIPS.divergent
    }
  ].filter(Boolean);
  const snapshotHeaderCellSx = {
    ...headerStyle(theme),
    bgcolor: "white",
    verticalAlign: "middle"
  };
  const snapshotBodyCellSx = {
    borderColor: alpha(theme.palette.divider, 0.1),
    overflow: "hidden",
    px: 1.25,
    py: 0.75,
    textOverflow: "ellipsis",
    verticalAlign: "middle",
    whiteSpace: "nowrap"
  };
  const snapshotControlCellSx = {
    ...snapshotBodyCellSx,
    height: 44,
    p: 0,
    textAlign: "center"
  };
  const snapshotHeaderControlCellSx = {
    ...snapshotHeaderCellSx,
    p: 0,
    textAlign: "center"
  };
  const snapshotFooterCellSx = {
    ...snapshotBodyCellSx,
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
    borderBottom: "none",
    borderTop: "1px solid",
    borderTopColor: "divider",
    fontWeight: 700
  };

  const displayValue = (key, value) => {
    if (key === "amount") return <FinancialAmount amount={value} />;
    if (key === "taxAdjustment") {
      const option = matchTaxAdjustmentOption(value, taxAdjustmentOptions);
      return option?.label || value || "None";
    }
    return <HighlightedMarkupText value={value} />;
  };

  const updateSnapshotRow = (rowIndex, fieldKey, nextValue) => {
    if (!onRowsChange) return;
    onRowsChange(
      rows.map((row, index) => {
        if (index !== rowIndex) return row;
        const nextHiddenValues = { ...(row._hiddenValues || {}) };
        delete nextHiddenValues[fieldKey];
        return {
          ...row,
          [fieldKey]: nextValue,
          _hiddenValues: nextHiddenValues
        };
      })
    );
  };

  const hideSnapshotValue = (rowIndex, fieldKey) => {
    if (!onRowsChange) return;
    onRowsChange(
      rows.map((row, index) => {
        if (index !== rowIndex) return row;
        if (String(row[fieldKey] ?? "") === SNAPSHOT_HIDDEN_VALUE) return row;
        return {
          ...row,
          [fieldKey]: SNAPSHOT_HIDDEN_VALUE,
          _hiddenValues: {
            ...(row._hiddenValues || {}),
            [fieldKey]: row[fieldKey]
          }
        };
      })
    );
  };

  const unhideSnapshotValue = (rowIndex, fieldKey) => {
    if (!onRowsChange) return;
    onRowsChange(
      rows.map((row, index) => {
        if (index !== rowIndex) return row;
        const restored = row._hiddenValues?.[fieldKey];
        const nextHiddenValues = { ...(row._hiddenValues || {}) };
        delete nextHiddenValues[fieldKey];
        return {
          ...row,
          [fieldKey]: restored ?? "",
          _hiddenValues: nextHiddenValues
        };
      })
    );
  };

  const toggleSnapshotRowHidden = (rowIndex) => {
    if (!onRowsChange) return;
    onRowsChange(
      rows.map((row, index) =>
        index === rowIndex ? { ...row, hiddenFromAi: !row.hiddenFromAi } : row
      )
    );
  };

  return (
    <Box sx={{ minWidth: 0, maxWidth: "100%", width: "100%" }}>
      {variant === "apply" && showProcessingSummary && (
        <ProcessingOptionSummary
          options={selectedProcessingOptions}
          rowCount={includedRows.length}
          totalNegative={totalNegative}
          totalText={totalText}
          variant={variant}
        />
      )}

      {variant === "apply" && (
        <SensitiveDataAlert
          fieldCount={sensitiveCounts.fieldCount}
          rowCount={sensitiveCounts.rowCount}
          sx={{ mb: 1.25 }}
        />
      )}

      {variant === "classify" && (
        <Box sx={{ mb: 1.5 }}>
          <SnapshotPurposePanel
            options={selectedProcessingOptions}
            includedCount={includedRows.length}
          />
        </Box>
      )}

      <Paper
        variant='outlined'
        sx={{
          borderRadius: 1.5,
          overflow: "hidden"
        }}
      >
        <Box
          sx={{
            alignItems: { xs: "stretch", sm: "center" },
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
            justifyContent: "space-between",
            px: 1.5,
            py: 1
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant='subtitle1' fontWeight={800} lineHeight={1.2}>
              Data snapshot
            </Typography>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: "block", mt: 0.35, lineHeight: 1.4 }}
            >
              {includedRows.length} line{includedRows.length === 1 ? "" : "s"} to
              send
              {` · Net ${totalText}`}
              {removedEmptyAmountCount > 0
                ? ` · ${removedEmptyAmountCount} empty-amount line${removedEmptyAmountCount === 1 ? "" : "s"} removed`
                : ""}
              {sensitiveCounts.fieldCount > 0
                ? ` · ${sensitiveCounts.fieldCount} placeholder${sensitiveCounts.fieldCount === 1 ? "" : "s"} to review`
                : ""}
              {hiddenRowCount > 0
                ? ` · ${hiddenRowCount} hidden row${hiddenRowCount === 1 ? "" : "s"}`
                : ""}
            </Typography>
          </Box>
          <Stack
            direction='row'
            spacing={0.5}
            alignItems='center'
            justifyContent='flex-end'
            sx={{ flexShrink: 0 }}
          >
            <Tooltip title='Zoom out'>
              <span>
                <IconButton
                  size='small'
                  aria-label='Zoom out'
                  disabled={zoom <= SNAPSHOT_ZOOM_MIN}
                  onClick={() =>
                    setZoom((current) =>
                      Math.max(SNAPSHOT_ZOOM_MIN, Number((current - SNAPSHOT_ZOOM_STEP).toFixed(2)))
                    )
                  }
                >
                  <ZoomOutIcon fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
            <Typography variant='caption' sx={{ minWidth: 40, textAlign: "center" }}>
              {Math.round(zoom * 100)}%
            </Typography>
            <Tooltip title='Zoom in'>
              <span>
                <IconButton
                  size='small'
                  aria-label='Zoom in'
                  disabled={zoom >= SNAPSHOT_ZOOM_MAX}
                  onClick={() =>
                    setZoom((current) =>
                      Math.min(SNAPSHOT_ZOOM_MAX, Number((current + SNAPSHOT_ZOOM_STEP).toFixed(2)))
                    )
                  }
                >
                  <ZoomInIcon fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title='Download Excel'>
              <IconButton
                size='small'
                aria-label='Download Excel'
                onClick={() => downloadSnapshotExcel(rows, columns)}
              >
                <FileDownloadOutlinedIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <TableContainer
          sx={{
            maxHeight: { xs: "48vh", sm: "54vh", md: "62vh" },
            maxWidth: "100%",
            overflow: "auto",
            WebkitOverflowScrolling: "touch"
          }}
        >
          <Table
            stickyHeader
            size='small'
            sx={{
              minWidth: Math.max(720, 88 + columns.length * 136),
              transform: `scale(${zoom})`,
              transformOrigin: "top left",
              width: `${100 / zoom}%`
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    ...snapshotHeaderControlCellSx,
                    borderRight: "1px solid",
                    borderRightColor: "divider",
                    left: 0,
                    maxWidth: 48,
                    minWidth: 48,
                    position: "sticky",
                    width: 48,
                    zIndex: 4
                  }}
                >
                  #
                </TableCell>
                {canEditSnapshot && (
                  <TableCell
                    sx={{
                      ...snapshotHeaderControlCellSx,
                      left: 48,
                      maxWidth: 44,
                      minWidth: 44,
                      position: "sticky",
                      width: 44,
                      zIndex: 4
                    }}
                  />
                )}
                {columns.map((key) => (
                  <TableCell
                    key={key}
                    align={key === "amount" ? "right" : "left"}
                    sx={{
                      ...snapshotHeaderCellSx,
                      ...(SNAPSHOT_COLUMN_SX[key] || { minWidth: 112 })
                    }}
                  >
                    {DOMAIN_LABELS[key] || key}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const hasSensitiveMarkup = Object.values(row).some(
                  containsSensitiveMarkup
                );
                const hiddenFromAi = Boolean(row.hiddenFromAi);
                return (
                  <TableRow
                    key={`snapshot-${index}`}
                    hover
                    sx={{
                      opacity: hiddenFromAi ? 0.45 : 1,
                      bgcolor: hiddenFromAi
                        ? (theme) => alpha(theme.palette.text.primary, 0.04)
                        : hasSensitiveMarkup
                          ? (theme) => alpha(theme.palette.error.main, 0.06)
                          : "inherit",
                      "&:hover": {
                        bgcolor: hiddenFromAi
                          ? (theme) => alpha(theme.palette.text.primary, 0.08)
                          : hasSensitiveMarkup
                            ? (theme) => alpha(theme.palette.error.main, 0.08)
                            : (theme) => alpha(theme.palette.primary.main, 0.04)
                      }
                    }}
                  >
                    <TableCell
                      sx={{
                        ...snapshotControlCellSx,
                        bgcolor: "background.paper",
                        borderRight: "1px solid",
                        borderRightColor: "divider",
                        color: "text.secondary",
                        left: 0,
                        maxWidth: 48,
                        minWidth: 48,
                        position: "sticky",
                        textDecoration: hiddenFromAi ? "line-through" : "none",
                        width: 48,
                        zIndex: 2
                      }}
                    >
                      {index + 1}
                    </TableCell>
                    {canEditSnapshot && (
                      <TableCell
                        sx={{
                          ...snapshotControlCellSx,
                          bgcolor: "background.paper",
                          left: 48,
                          maxWidth: 44,
                          minWidth: 44,
                          position: "sticky",
                          width: 44,
                          zIndex: 2
                        }}
                      >
                        <Tooltip
                          title={
                            hiddenFromAi
                              ? "Unhide this row"
                              : "Hide this row from AI"
                          }
                        >
                          <IconButton
                            size='small'
                            aria-label={
                              hiddenFromAi
                                ? "Unhide this row"
                                : "Hide this row from AI"
                            }
                            onClick={() => toggleSnapshotRowHidden(index)}
                            sx={{ height: 28, width: 28 }}
                          >
                            {hiddenFromAi ? (
                              <VisibilityIcon sx={{ fontSize: 16 }} />
                            ) : (
                              <VisibilityOffIcon sx={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                    {columns.map((key) => (
                      <TableCell
                        key={key}
                        align={key === "amount" ? "right" : "left"}
                        sx={{
                          ...snapshotBodyCellSx,
                          ...(SNAPSHOT_COLUMN_SX[key] || { minWidth: 112 })
                        }}
                      >
                        {canEditSnapshot ? (
                          <SnapshotEditableValue
                            fieldKey={key}
                            value={row[key]}
                            taxAdjustmentOptions={taxAdjustmentOptions}
                            onCommit={(nextValue) =>
                              updateSnapshotRow(index, key, nextValue)
                            }
                            onMask={() => hideSnapshotValue(index, key)}
                            onUnmask={() => unhideSnapshotValue(index, key)}
                          />
                        ) : (
                          displayValue(key, row[key])
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
            {columns.includes("amount") && (
              <TableFooter>
                <TableRow>
                  <TableCell
                    colSpan={
                      (canEditSnapshot ? 2 : 1) + columns.indexOf("amount")
                    }
                    sx={{
                      ...snapshotFooterCellSx
                    }}
                  >
                    Total
                  </TableCell>
                  <TableCell
                    align='right'
                    sx={{
                      ...snapshotFooterCellSx,
                      ...SNAPSHOT_COLUMN_SX.amount
                    }}
                  >
                    <FinancialAmount amount={totalAmount} />
                  </TableCell>
                  {columns.slice(columns.indexOf("amount") + 1).map((key) => (
                    <TableCell
                      key={key}
                      sx={{
                        ...snapshotFooterCellSx,
                        ...(SNAPSHOT_COLUMN_SX[key] || {})
                      }}
                    />
                  ))}
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Paper>

      {variant === "classify" && (
        <Box sx={{ mt: 1.5 }}>
          <SnapshotReviewNotes
            removedEmptyAmountCount={removedEmptyAmountCount}
            sensitiveCounts={sensitiveCounts}
            hiddenRowCount={hiddenRowCount}
            hiddenValueCount={hiddenValueCount}
          />
        </Box>
      )}
    </Box>
  );
};

const renderTaxAdjustmentMenuItems = (taxAdjustmentOptions) => {
  const nodes = [];
  let currentGroup = null;

  taxAdjustmentOptions.forEach((option) => {
    if (option.group && option.group !== currentGroup) {
      currentGroup = option.group;
      nodes.push(
        <ListSubheader key={`group-${option.group}`} disableSticky>
          {option.group}
        </ListSubheader>
      );
    }
    nodes.push(
      <MenuItem
        key={`${option.group}-${option.value || option.label}`}
        value={option.value}
      >
        {option.label}
      </MenuItem>
    );
  });

  return nodes;
};

const DivergenceControl = ({ value, onChange }) => {
  const selected = getDivergentLevel(value);
  const sliderValue = Math.max(
    0,
    DIVERGENT_LEVELS.findIndex((level) => level.value === selected.value)
  );

  return (
    <Box sx={{ mt: 1, px: 0.25 }}>
      <Stack direction='row' alignItems='center' spacing={0.5} sx={{ mb: 0.25 }}>
        <Typography variant='caption' color='text.secondary' fontWeight={700}>
          Inference strictness
        </Typography>
        <JustInfo title={TOOLTIPS.divergent} ml={0} mr={0} />
      </Stack>
      <Slider
        value={sliderValue}
        min={0}
        max={DIVERGENT_LEVELS.length - 1}
        step={1}
        marks={DIVERGENT_SLIDER_MARKS}
        onChange={(_event, nextValue) => {
          const index = Array.isArray(nextValue) ? nextValue[0] : nextValue;
          onChange(DIVERGENT_LEVELS[index]?.value || DEFAULT_DIVERGENT);
        }}
        valueLabelDisplay='off'
        sx={{
          mx: 0.5,
          width: "calc(100% - 8px)",
          "& .MuiSlider-markLabel": {
            fontSize: "0.7rem",
            top: 24
          }
        }}
      />
      <Typography variant='caption' fontWeight={700} sx={{ display: "block", mt: 1.5 }}>
        {selected.mode}
      </Typography>
      <Typography variant='caption' color='text.secondary' display='block'>
        {selected.description}
      </Typography>
    </Box>
  );
};

const ColumnSelectionReviewStep = ({
  extractionResult,
  companyType,
  onBack,
  onConfirm
}) => {
  const theme = useTheme();
  const taxAdjustmentOptions = useMemo(
    () => getTaxAdjustmentOptions(companyType),
    [companyType]
  );
  const sheets = useMemo(
    () => getExtractedSheets(extractionResult),
    [extractionResult]
  );
  const [selectedSheetName, setSelectedSheetName] = useState(
    sheets[0]?.name || ""
  );
  const selectedSheet = useMemo(
    () =>
      sheets.find((sheet) => sheet.name === selectedSheetName) ||
      sheets[0] ||
      null,
    [sheets, selectedSheetName]
  );
  const initialTable = useMemo(
    () => buildTableState(selectedSheet),
    [selectedSheet]
  );

  const [columnNames, setColumnNames] = useState(initialTable.columnNames);
  const [rows, setRows] = useState(initialTable.rows);
  const [columnMapping, setColumnMapping] = useState({});
  const [processPlCategory, setProcessPlCategory] = useState(true);
  const [processTaxAdjustment, setProcessTaxAdjustment] = useState(false);
  const [mergeAccounts, setMergeAccounts] = useState(false);
  const [divergent, setDivergent] = useState(DEFAULT_DIVERGENT);
  const [editingCell, setEditingCell] = useState(null);
  const [snapshotRows, setSnapshotRows] = useState(null);
  const [snapshotRemovedCount, setSnapshotRemovedCount] = useState(0);
  const [aiProcessingConsentAccepted, setAiProcessingConsentAccepted] =
    useState(false);
  const [isProcessingColumn, setIsProcessingColumn] = useState(false);
  const columnProcessingRef = useRef({ active: false, requestId: 0 });

  useEffect(() => {
    setSelectedSheetName(sheets[0]?.name || "");
  }, [sheets]);

  useEffect(() => {
    columnProcessingRef.current.requestId += 1;
    columnProcessingRef.current.active = false;
    setIsProcessingColumn(false);
    setColumnNames([...initialTable.columnNames]);
    setRows(cloneTableRows(initialTable.rows));
    setColumnMapping({});
    setProcessPlCategory(true);
    setProcessTaxAdjustment(false);
    setMergeAccounts(false);
    setDivergent(DEFAULT_DIVERGENT);
    setEditingCell(null);
    setSnapshotRows(null);
    setSnapshotRemovedCount(0);
    setAiProcessingConsentAccepted(false);
  }, [initialTable]);

  const assignedKeys = useMemo(
    () => new Set(Object.values(columnMapping).filter(Boolean)),
    [columnMapping]
  );
  const amountAssignedCount = useMemo(
    () =>
      Object.values(columnMapping).filter((domainKey) => domainKey === "amount")
        .length,
    [columnMapping]
  );
  const mergeAccountsEnabled = processPlCategory && mergeAccounts;
  const hasAiProcessingOption =
    processPlCategory || processTaxAdjustment || mergeAccountsEnabled;
  const canProceed =
    assignedKeys.has("accountName") &&
    assignedKeys.has("amount") &&
    hasAiProcessingOption;
  const proceedHint =
    !assignedKeys.has("accountName") || !assignedKeys.has("amount")
      ? "Account Name and Amount are required. Map both columns before continuing."
      : !hasAiProcessingOption
        ? "Turn on at least one AI processing option before continuing."
        : amountAssignedCount > 1
          ? `${amountAssignedCount} Amount columns will be summed into one amount.`
          : "Review selected rows before AI processing.";
  const includedCount = rows.filter((row) => row.included).length;
  const allRowsIncluded = rows.length > 0 && includedCount === rows.length;
  const someRowsIncluded = includedCount > 0 && !allRowsIncluded;
  const sensitiveCounts = useMemo(() => countSensitiveMarkup(rows), [rows]);
  const snapshotSensitiveCounts = useMemo(
    () => countSensitiveMarkup(snapshotRows || []),
    [snapshotRows]
  );
  const tableHeaderCellSx = {
    ...headerStyle(theme),
    verticalAlign: "middle",
    bgcolor: "white"
  };
  const tableBodyCellSx = {
    borderColor: alpha(theme.palette.divider, 0.1),
    overflow: "hidden",
    px: 1.25,
    py: 0.75,
    textOverflow: "ellipsis",
    verticalAlign: "middle",
    whiteSpace: "nowrap"
  };
  const tableControlCellSx = {
    ...tableBodyCellSx,
    height: 44,
    p: 0,
    textAlign: "center"
  };
  const headerControlCellSx = {
    ...tableHeaderCellSx,
    p: 0,
    textAlign: "center"
  };
  const renderColumnMappingValue = (selected) => {
    if (!selected) return <PlaceholderText>Unassigned</PlaceholderText>;
    const domainKey = DOMAIN_KEYS.find((item) => item.key === selected);
    return (
      <Box
        component='span'
        sx={{
          color: domainKey?.required ? "primary.main" : "inherit",
          fontWeight: 600
        }}
      >
        {domainKey?.label || selected}
        {domainKey?.required ? " *" : ""}
      </Box>
    );
  };
  const buildColumnMappingOptions = (selectedKey) => [
    {
      value: "",
      label: <PlaceholderText>Unassigned</PlaceholderText>
    },
    ...ORDERED_DOMAIN_KEYS.map((domainKey) => {
      const assignedElsewhere =
        UNIQUE_DOMAIN_KEYS.has(domainKey.key) &&
        assignedKeys.has(domainKey.key) &&
        selectedKey !== domainKey.key;
      const amountAlreadyMapped =
        domainKey.key === "amount" && amountAssignedCount > 0;

      return {
        value: domainKey.key,
        disabled: assignedElsewhere,
        label: (
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            spacing={1}
            sx={{ width: "100%" }}
          >
            <Typography
              variant='body2'
              sx={{
                color: domainKey.required ? "primary.main" : "inherit",
                fontWeight: domainKey.required ? 700 : 400
              }}
            >
              {domainKey.label}
            </Typography>
            <Stack direction='row' spacing={0.5} alignItems='center'>
              {assignedElsewhere && (
                <Typography variant='caption' color='text.secondary'>
                  Assigned
                </Typography>
              )}
              {amountAlreadyMapped && selectedKey !== "amount" && (
                <Typography variant='caption' color='text.secondary'>
                  Add another
                </Typography>
              )}
              {domainKey.key === "amount" &&
                selectedKey === "amount" &&
                amountAssignedCount > 1 && (
                  <Typography variant='caption' color='text.secondary'>
                    {amountAssignedCount} mapped
                  </Typography>
                )}
              {domainKey.required && (
                <Chip color='primary' label='Required' size='small' />
              )}
            </Stack>
          </Stack>
        ),
        sx: domainKey.required
          ? {
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              "&.Mui-focusVisible, &:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.1)
              }
            }
          : undefined
      };
    })
  ];

  useEffect(() => {
    if (!processPlCategory) setMergeAccounts(false);
  }, [processPlCategory]);

  const applyColumnAssignment = (columnName, domainKey) => {
    const displacedColumns =
      domainKey && UNIQUE_DOMAIN_KEYS.has(domainKey)
        ? Object.entries(columnMapping)
            .filter(
              ([mappedColumn, mappedDomainKey]) =>
                mappedColumn !== columnName && mappedDomainKey === domainKey
            )
            .map(([mappedColumn]) => mappedColumn)
        : [];
    const nextMapping = { ...columnMapping };
    displacedColumns.forEach((mappedColumn) => {
      nextMapping[mappedColumn] = "";
    });
    nextMapping[columnName] = domainKey;
    setColumnMapping(nextMapping);

    setRows((previous) =>
      previous.map((row) => {
        const cells = { ...row.cells };
        displacedColumns.forEach((mappedColumn) => {
          cells[mappedColumn] = row.originalCells?.[mappedColumn] ?? "";
        });
        const originalValue = row.originalCells?.[columnName] ?? "";
        cells[columnName] = domainKey
          ? coerceCellValue(domainKey, originalValue, taxAdjustmentOptions)
          : originalValue;
        return { ...row, cells };
      })
    );
  };

  const requestColumnAssignment = (columnName, domainKey) => {
    if (columnProcessingRef.current.active) return;

    columnProcessingRef.current.active = true;
    columnProcessingRef.current.requestId += 1;
    const requestId = columnProcessingRef.current.requestId;
    setIsProcessingColumn(true);

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (requestId !== columnProcessingRef.current.requestId) return;

        try {
          applyColumnAssignment(columnName, domainKey);
        } finally {
          if (requestId === columnProcessingRef.current.requestId) {
            columnProcessingRef.current.active = false;
            setIsProcessingColumn(false);
          }
        }
      });
    });
  };

  const resetReview = () => {
    columnProcessingRef.current.requestId += 1;
    columnProcessingRef.current.active = false;
    setIsProcessingColumn(false);
    setColumnNames([...initialTable.columnNames]);
    setRows(cloneTableRows(initialTable.rows));
    setColumnMapping({});
    setProcessPlCategory(true);
    setProcessTaxAdjustment(false);
    setMergeAccounts(false);
    setDivergent(DEFAULT_DIVERGENT);
    setEditingCell(null);
    setSnapshotRows(null);
    setSnapshotRemovedCount(0);
    setAiProcessingConsentAccepted(false);
  };

  const updateCell = (rowId, columnName, value) => {
    setRows((previous) =>
      previous.map((row) =>
        row.id === rowId
          ? { ...row, cells: { ...row.cells, [columnName]: value } }
          : row
      )
    );
  };

  const toggleRowIncluded = (rowId) => {
    setRows((previous) =>
      previous.map((row) =>
        row.id === rowId ? { ...row, included: !row.included } : row
      )
    );
  };

  const toggleAllRowsIncluded = (included) => {
    setRows((previous) => previous.map((row) => ({ ...row, included })));
  };

  const moveRow = (rowIndex, offset) => {
    const nextIndex = rowIndex + offset;
    if (nextIndex < 0 || nextIndex >= rows.length) return;
    setRows((previous) => {
      const next = [...previous];
      const [moved] = next.splice(rowIndex, 1);
      next.splice(nextIndex, 0, moved);
      return next;
    });
  };

  const handleAmountChange = (rowId, columnName, value) => {
    if (PARTIAL_AMOUNT_PATTERN.test(value)) {
      updateCell(rowId, columnName, value);
      return;
    }
    const coerced = coerceCellValue("amount", value);
    if (coerced !== "") updateCell(rowId, columnName, coerced);
  };

  const handleAmountBlur = (rowId, columnName, value) => {
    updateCell(rowId, columnName, coerceCellValue("amount", value));
    setEditingCell(null);
  };

  const openSnapshot = () => {
    const result = buildNormalizedRows(rows, columnMapping);
    setAiProcessingConsentAccepted(false);
    setSnapshotRows(result.rows);
    setSnapshotRemovedCount(result.removedEmptyAmountCount);
  };

  const closeSnapshot = () => {
    setSnapshotRows(null);
    setSnapshotRemovedCount(0);
    setAiProcessingConsentAccepted(false);
  };

  const confirmSnapshot = () => {
    if (!aiProcessingConsentAccepted) return;
    const finalPayload = sanitizeSnapshotForAi(snapshotRows || []);
    const processingOptions = {
      plCategory: processPlCategory,
      taxAdjustment: processTaxAdjustment,
      mergeAccounts: mergeAccountsEnabled,
      divergent,
      ...(processTaxAdjustment && {
        taxComputationType: getTaxComputationType(companyType)
      }),
      dataReviewConsent: {
        version: AI_PROCESSING_CONSENT_VERSION,
        accepted: true,
        acceptedAt: new Date().toISOString(),
        redactedFieldCount: snapshotSensitiveCounts.fieldCount,
        redactedRowCount: snapshotSensitiveCounts.rowCount
      }
    };
    closeSnapshot();
    onConfirm?.(finalPayload, processingOptions);
  };

  if (!columnNames.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity='warning' sx={{ mb: 2 }}>
          No extracted columns were found. Go back and upload a document.
        </Alert>
        {onBack && (
          <Button onClick={onBack} sx={{ textTransform: "none" }}>
            Back
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        p: 0.5,
        width: "100%"
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        sx={{ flex: 1, minHeight: 0, minWidth: 0, width: "100%" }}
      >
        <MappingSettings
          onBack={onBack}
          description='Map each extracted column to the correct field. Amount can be assigned to more than one column; those values are summed into one amount.'
          sensitiveData={sensitiveCounts}
          sheets={sheets}
          selectedSheetName={selectedSheet?.name || ""}
          onSheetChange={setSelectedSheetName}
          processingDescription='Choose at least one task.'
          processingOptions={[
            {
              id: "plCategory",
              checked: processPlCategory,
              onChange: setProcessPlCategory,
              label: "Assign P&L Sections",
              tooltip: TOOLTIPS.plCategory
            },
            {
              id: "taxAdjustment",
              checked: processTaxAdjustment,
              onChange: setProcessTaxAdjustment,
              label: "Suggest Tax Treatment",
              tooltip: TOOLTIPS.taxAdjustment
            },
            {
              id: "mergeAccounts",
              checked: mergeAccountsEnabled,
              disabled: !processPlCategory,
              onChange: setMergeAccounts,
              label: "Merge Similar Accounts",
              tooltip: processPlCategory
                ? TOOLTIPS.mergeAccounts
                : `${TOOLTIPS.mergeAccounts} Turn on P&L Category to enable this option.`
            }
          ]}
          processingControls={
            <DivergenceControl value={divergent} onChange={setDivergent} />
          }
          processingError={
            !hasAiProcessingOption
              ? "Turn on at least one option to continue."
              : undefined
          }
          assignmentWarning={ASSIGNMENT_WARNING}
          canProceed={canProceed}
          busy={isProcessingColumn}
          proceedHint={proceedHint}
          includedCount={includedCount}
          onProceed={openSnapshot}
          onReset={resetReview}
        />

        <Box
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: "column",
            minHeight: { xs: 240, md: 0 },
            minWidth: 0,
            overflow: "hidden",
            position: "relative",
            width: "100%"
          }}
        >
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{
              display: { xs: "block", lg: "none" },
              flexShrink: 0,
              mb: 0.75,
              px: 0.25
            }}
          >
            Swipe or scroll horizontally to see all columns.
          </Typography>
          <TableContainer
            component={Paper}
            variant='outlined'
            sx={{
              borderRadius: 0,
              boxShadow: "none",
              flex: 1,
              maxHeight: { xs: "52vh", md: "none" },
              maxWidth: "100%",
              minHeight: 0,
              overflowX: "auto",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              "& .MuiTable-root": {
                minWidth: Math.max(760, 136 + columnNames.length * 160),
                width: "max-content"
              }
            }}
          >
          <Table stickyHeader size='small'>
            <TableHead>
              <TableRow>
                <TableCell
                  padding='checkbox'
                  sx={{
                    ...headerControlCellSx,
                    borderRight: "1px solid",
                    borderRightColor: "divider",
                    left: 0,
                    maxWidth: 48,
                    minWidth: 48,
                    position: "sticky",
                    width: 48,
                    zIndex: 4
                  }}
                >
                  <Checkbox
                    size='small'
                    checked={allRowsIncluded}
                    indeterminate={someRowsIncluded}
                    onChange={(event) =>
                      toggleAllRowsIncluded(event.target.checked)
                    }
                    inputProps={{ "aria-label": "Select all rows" }}
                  />
                </TableCell>
                {columnNames.map((columnName) => {
                  const selectedKey = columnMapping[columnName] || "";
                  return (
                    <TableCell
                      key={columnName}
                      align='center'
                      sx={{
                        ...tableHeaderCellSx,
                        minWidth: 160,
                        width: 160
                      }}
                    >
                      <DropDownByObj
                        label=''
                        size='small'
                        maxWidth='100%'
                        variant='standard'
                        displayEmpty
                        formControlSx={{
                          height: "100%",
                          justifyContent: "center"
                        }}
                        value={selectedKey}
                        options={buildColumnMappingOptions(selectedKey)}
                        renderValue={renderColumnMappingValue}
                        onChange={(event) =>
                          requestColumnAssignment(
                            columnName,
                            event.target.value
                          )
                        }
                        sx={{
                          minHeight: 32,
                          "&:before": {
                            borderBottomColor: alpha(
                              theme.palette.text.primary,
                              0.18
                            )
                          },
                          "& .MuiSelect-select": {
                            alignItems: "center",
                            display: "flex",
                            fontWeight: 600,
                            justifyContent: "center",
                            minHeight: "24px !important",
                            textAlign: "center",
                            py: 0.25
                          }
                        }}
                      />
                    </TableCell>
                  );
                })}
                <TableCell
                  align='center'
                  sx={{
                    ...headerControlCellSx,
                    maxWidth: 88,
                    minWidth: 88,
                    width: 88
                  }}
                >
                  Order
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, rowIndex) => {
                const hasSensitiveMarkup = Object.values(row.cells || {}).some(
                  containsSensitiveMarkup
                );
                return (
                  <TableRow
                    key={row.id}
                    sx={{
                      opacity: row.included ? 1 : 0.45,
                      bgcolor: hasSensitiveMarkup
                        ? (theme) => alpha(theme.palette.error.main, 0.06)
                        : "inherit",
                      "&:hover": {
                        bgcolor: hasSensitiveMarkup
                          ? (theme) => alpha(theme.palette.error.main, 0.08)
                          : (theme) => alpha(theme.palette.primary.main, 0.04)
                      }
                    }}
                  >
                    <TableCell
                      padding='checkbox'
                      sx={{
                        ...tableControlCellSx,
                        bgcolor: "background.paper",
                        borderRight: "1px solid",
                        borderRightColor: "divider",
                        left: 0,
                        maxWidth: 48,
                        minWidth: 48,
                        position: "sticky",
                        width: 48,
                        zIndex: 2
                      }}
                    >
                      <Checkbox
                        size='small'
                        checked={row.included}
                        onChange={() => toggleRowIncluded(row.id)}
                      />
                    </TableCell>
                    {columnNames.map((columnName) => {
                      const domainKey = columnMapping[columnName] || "";
                      const value = row.cells[columnName] ?? "";
                      const isEditing =
                        editingCell?.rowId === row.id &&
                        editingCell?.columnName === columnName;

                      if (
                        domainKey === "taxAdjustment" ||
                        domainKey === "section"
                      ) {
                        const selectValue =
                          domainKey === "taxAdjustment"
                            ? (matchTaxAdjustmentOption(
                                value,
                                taxAdjustmentOptions
                              )?.value ?? "")
                            : value;
                        return (
                          <TableCell
                            key={columnName}
                            sx={{ ...tableBodyCellSx, py: 0.5 }}
                          >
                            <Select
                              size='small'
                              fullWidth
                              variant='standard'
                              displayEmpty
                              value={selectValue}
                              renderValue={(selected) => {
                                if (domainKey === "taxAdjustment") {
                                  return (
                                    matchTaxAdjustmentOption(
                                      selected,
                                      taxAdjustmentOptions
                                    )?.label || (
                                      <PlaceholderText>Select</PlaceholderText>
                                    )
                                  );
                                }
                                if (!selected) {
                                  return (
                                    <PlaceholderText>Select</PlaceholderText>
                                  );
                                }
                                return selected;
                              }}
                              onChange={(event) =>
                                updateCell(
                                  row.id,
                                  columnName,
                                  event.target.value
                                )
                              }
                              sx={{
                                "& .MuiSelect-select": {
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap"
                                }
                              }}
                            >
                              {domainKey === "taxAdjustment"
                                ? renderTaxAdjustmentMenuItems(
                                    taxAdjustmentOptions
                                  )
                                : [
                                    <MenuItem key='section-empty' value=''>
                                      <PlaceholderText>Select</PlaceholderText>
                                    </MenuItem>,
                                    ...SECTION_OPTIONS.map((option) => (
                                      <MenuItem key={option} value={option}>
                                        {option}
                                      </MenuItem>
                                    ))
                                  ]}
                            </Select>
                          </TableCell>
                        );
                      }

                      if (domainKey === "amount") {
                        const originallyEmpty = isOriginallyEmptyAmount(
                          row,
                          columnName
                        );
                        return (
                          <TableCell
                            key={columnName}
                            align='right'
                            sx={{ ...tableBodyCellSx, py: 0.25 }}
                          >
                            {originallyEmpty ? (
                              <Typography
                                variant='body2'
                                color='text.secondary'
                                sx={{ textAlign: "right" }}
                              >
                                —
                              </Typography>
                            ) : (
                              <TextField
                                variant='standard'
                                size='small'
                                fullWidth
                                value={value}
                                onChange={(event) =>
                                  handleAmountChange(
                                    row.id,
                                    columnName,
                                    event.target.value
                                  )
                                }
                                onBlur={(event) =>
                                  handleAmountBlur(
                                    row.id,
                                    columnName,
                                    event.target.value
                                  )
                                }
                                inputProps={{
                                  inputMode: "decimal"
                                }}
                                sx={{
                                  "& .MuiInput-underline:before": {
                                    borderBottomColor: "transparent"
                                  },
                                  "& .MuiInput-input": {
                                    textAlign: "right",
                                    color:
                                      Number(value) < 0
                                        ? "error.main"
                                        : "inherit",
                                    fontWeight: Number(value) < 0 ? 600 : 400
                                  }
                                }}
                              />
                            )}
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell
                          key={columnName}
                          onClick={() =>
                            setEditingCell({ rowId: row.id, columnName })
                          }
                          sx={{
                            ...tableBodyCellSx,
                            cursor: "text",
                            px: 0.75,
                            py: 0.25
                          }}
                        >
                          {isEditing ? (
                            <TextField
                              variant='standard'
                              size='small'
                              fullWidth
                              autoFocus
                              value={value}
                              onChange={(event) =>
                                updateCell(
                                  row.id,
                                  columnName,
                                  event.target.value
                                )
                              }
                              onBlur={() => setEditingCell(null)}
                            />
                          ) : (
                            <Stack
                              direction='row'
                              alignItems='center'
                              spacing={0.5}
                              sx={{
                                px: 0.5,
                                py: 0.25,
                                borderRadius: 1,
                                minHeight: 28,
                                "& .edit-icon": { opacity: 0 },
                                "&:hover": { bgcolor: "action.hover" },
                                "&:hover .edit-icon": { opacity: 0.55 }
                              }}
                            >
                              <Typography
                                variant='body2'
                                noWrap
                                sx={{ flex: 1 }}
                              >
                                <HighlightedMarkupText value={value} />
                              </Typography>
                              <EditOutlinedIcon
                                className='edit-icon'
                                sx={{ fontSize: 14, color: "text.secondary" }}
                              />
                            </Stack>
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell
                      align='center'
                      sx={{
                        ...tableControlCellSx,
                        maxWidth: 88,
                        minWidth: 88,
                        width: 88
                      }}
                    >
                      <Stack direction='row' justifyContent='center'>
                        <IconButton
                          size='small'
                          aria-label='Move row up'
                          disabled={rowIndex === 0}
                          onClick={() => moveRow(rowIndex, -1)}
                          sx={{ height: 32, width: 32 }}
                        >
                          <KeyboardArrowUpIcon fontSize='small' />
                        </IconButton>
                        <IconButton
                          size='small'
                          aria-label='Move row down'
                          disabled={rowIndex === rows.length - 1}
                          onClick={() => moveRow(rowIndex, 1)}
                          sx={{ height: 32, width: 32 }}
                        >
                          <KeyboardArrowDownIcon fontSize='small' />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </TableContainer>
          {isProcessingColumn && (
            <Box
              role='status'
              aria-live='polite'
              aria-label='Processing column values'
              sx={{
                alignItems: "center",
                backdropFilter: "blur(3px)",
                WebkitBackdropFilter: "blur(3px)",
                bgcolor: alpha(theme.palette.background.paper, 0.58),
                cursor: "progress",
                display: "flex",
                inset: 0,
                justifyContent: "center",
                position: "absolute",
                zIndex: 5
              }}
            >
              <Stack alignItems='center' spacing={1}>
                <CircularProgress size={38} thickness={4} />
                <Typography variant='caption' color='text.secondary' fontWeight={600}>
                  Processing column values...
                </Typography>
              </Stack>
            </Box>
          )}
        </Box>
      </Stack>

      <BaseDialog
        open={snapshotRows !== null}
        onClose={closeSnapshot}
        icon={<InsightsIcon fontSize='small' />}
        title='Confirm AI Processing'
        subtitle='AI will classify these accounts. Review the snapshot before sending.'
        maxWidth='lg'
        paperSx={{
          display: "flex",
          flexDirection: "column",
          m: { xs: 1, sm: 2 },
          width: { xs: "calc(100% - 16px)", sm: "calc(100% - 32px)" },
          height: { xs: "calc(100dvh - 16px)", md: "calc(100dvh - 32px)" },
          maxHeight: { xs: "calc(100dvh - 16px)", md: "calc(100dvh - 32px)" },
          minHeight: 0,
          overflow: "hidden"
        }}
        contentSx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          minWidth: 0,
          overflowX: "hidden",
          overflowY: "auto",
          px: { xs: 2, sm: 3 },
          py: 2
        }}
        actions={
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, width: "100%" }}>
            <AiProcessingConsent
              checked={aiProcessingConsentAccepted}
              onChange={setAiProcessingConsentAccepted}
            />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              justifyContent='flex-end'
            >
              <Button
                onClick={closeSnapshot}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Back to edit
              </Button>
              <Tooltip
                title={
                  aiProcessingConsentAccepted
                    ? undefined
                    : "Confirm the snapshot is ready to continue."
                }
                placement='top'
              >
                <span>
                  <Button
                    variant='contained'
                    onClick={confirmSnapshot}
                    disabled={!aiProcessingConsentAccepted}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                  >
                    Confirm & continue
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Box>
        }
        actionsSx={{
          px: { xs: 2, sm: 3 },
          py: 1.5,
          flexWrap: "wrap",
          gap: 1,
          justifyContent: "stretch",
          "& > :not(style)": {
            m: 0
          }
        }}
      >
        <SnapshotReviewTable
          rows={snapshotRows || []}
          taxAdjustmentOptions={taxAdjustmentOptions}
          processingOptions={{
            plCategory: processPlCategory,
            taxAdjustment: processTaxAdjustment,
            mergeAccounts: mergeAccountsEnabled,
            divergent,
            ...(processTaxAdjustment && {
              taxComputationType: getTaxComputationType(companyType)
            })
          }}
          removedEmptyAmountCount={snapshotRemovedCount}
          sensitiveCounts={snapshotSensitiveCounts}
          onRowsChange={setSnapshotRows}
        />
      </BaseDialog>
    </Box>
  );
};

export {
  DOMAIN_KEYS,
  SECTION_OPTIONS,
  SnapshotReviewTable,
  SensitiveDataAlert,
  buildNormalizedRows,
  coerceCellValue,
  getTaxAdjustmentOptions,
  getTaxComputationType,
  getExtractedSheets,
  matchTaxAdjustmentOption
};
export default ColumnSelectionReviewStep;
